import {
  LayoutDashboard,
  Building2,
  Receipt,
  Wallet,
  ArrowLeft,
  HelpCircle,
  FileText,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountMenu } from '@/components/layout/AccountMenu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const menuGroups = [
  {
    label: 'Admin',
    items: [
      { title: 'Dashboard', url: '/a12o', icon: LayoutDashboard },
      { title: 'Tenants', url: '/a12o/tenants', icon: Building2 },
    ],
  },
  {
    label: 'Faturamento',
    items: [
      { title: 'Assinaturas', url: '/a12o/assinaturas', icon: Receipt },
      { title: 'Financeiro', url: '/a12o/financeiro', icon: Wallet },
    ],
  },
  {
    label: 'Conteúdo',
    items: [
      { title: 'Blog', url: '/a12o/blog', icon: FileText },
      { title: 'Central de Ajuda', url: '/a12o/ajuda', icon: HelpCircle },
    ],
  },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const isActive = (path: string) => currentPath === path;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className={`border-b border-sidebar-border ${collapsed ? 'p-3 flex items-center justify-center' : 'p-4'}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <img
              src="/logos/hermys-icon-small-lilac.png"
              alt="Hermys"
              className="h-8 w-8"
            />
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-sidebar-foreground">
                Hermys
              </span>
              <span className="text-xs text-muted-foreground">
                Painel Admin
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-1">
            <img
              src="/logos/hermys-icon-small-lilac.png"
              alt="Hermys"
              className="h-9 w-9"
            />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {menuGroups.map((group) => (
          <SidebarGroup key={group.label} className={collapsed ? 'flex items-center' : ''}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className={collapsed ? 'items-center gap-2' : ''}>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title} className={collapsed ? 'flex justify-center' : ''}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                      className={cn(
                        "group/menu transition-all duration-150",
                        isActive(item.url) && "bg-sidebar-accent text-sidebar-primary font-medium hover:bg-sidebar-accent hover:text-sidebar-primary"
                      )}
                    >
                      <NavLink to={item.url} end>
                        <item.icon className="h-5 w-5 transition-transform duration-200 group-hover/menu:scale-110" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup className={collapsed ? 'flex items-center' : ''}>
          <SidebarGroupContent>
            <SidebarMenu className={collapsed ? 'items-center' : ''}>
              <SidebarMenuItem className={collapsed ? 'flex justify-center' : ''}>
                <SidebarMenuButton asChild tooltip="Voltar ao App" className="group/menu">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground"
                    onClick={() => navigate('/')}
                  >
                    <ArrowLeft className="h-5 w-5 transition-transform duration-200 group-hover/menu:scale-110" />
                    {!collapsed && <span>Voltar ao App</span>}
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className={`border-t border-sidebar-border ${collapsed ? 'p-2 flex items-center justify-center' : 'p-4'}`}>
        <AccountMenu />
      </SidebarFooter>
    </Sidebar>
  );
}
